import DOMPurify from 'dompurify';
import { isEmbeddableUrl, isUploadIframeSrc, UPLOAD_CLOUDFRONT_HOSTS, UPLOAD_S3_HOSTS } from '../utils/embedUrl';
import { uploadedFileName } from '../utils/mediaFileName';
import { FORUM_SANITIZE_CONFIG } from './sanitizeConfig';

// gloss
// why: Third-party embed iframes must never get allow-same-origin — without dropping it, a
// why: scripted player could read forum cookies/storage.
const PROVIDER_SANDBOX_FLAGS = 'allow-scripts allow-popups';

// gloss
type IframeKind = 'pdf' | 'provider' | null;

// gloss
// why: An upload-origin iframe that isn't a .pdf must never fall to the provider sandbox — that
// why: grants allow-scripts to hosted HTML.
const UPLOAD_ORIGIN_HOSTS = new Set<string>([...UPLOAD_CLOUDFRONT_HOSTS, ...UPLOAD_S3_HOSTS]);

// gloss
// why: Provider classification must check the path is an actual embed route, not just the
// why: hostname — host-only matching grants allow-scripts.
const PROVIDER_EMBED_PATH = /(^\/(embed|player|video|show)(\/|$))|(\/embed$)/i;

const isProviderIframeSrc = (rawSrc: string, pathname: string): boolean =>
  isEmbeddableUrl(rawSrc) && PROVIDER_EMBED_PATH.test(pathname);

// gloss
const classifyIframeSrc = (rawSrc: string | null): IframeKind => {
  if (!rawSrc) return null;
  let parsed: URL;
  try {
    parsed = new URL(rawSrc);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  if (isUploadIframeSrc(rawSrc)) return 'pdf';
  if (UPLOAD_ORIGIN_HOSTS.has(parsed.hostname.toLowerCase())) return null;
  if (isProviderIframeSrc(rawSrc, parsed.pathname)) return 'provider';
  return null;
};

type LinkElement = { setAttribute: (name: string, value: string) => void; textContent: string | null };

type LinkDocument = { createElement: (tagName: 'a') => LinkElement };

// gloss
const pdfDownloadLink = (ownerDocument: LinkDocument, rawSrc: string): LinkElement => {
  const url = new URL(rawSrc);
  url.hash = '';
  if ((UPLOAD_S3_HOSTS as readonly string[]).includes(url.hostname.toLowerCase())) {
    url.hostname = UPLOAD_CLOUDFRONT_HOSTS[0];
  }
  const link = ownerDocument.createElement('a');
  link.setAttribute('href', url.toString());
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer nofollow');
  link.textContent = `PDF · ${uploadedFileName(url)} · Download`;
  return link;
};

// gloss
export const sanitizeForumPostHtml = (html: string | null | undefined): string => {
  if (!html) return '';

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'IFRAME') return;
    const src = node.getAttribute('src');
    const kind = classifyIframeSrc(src);
    const parent = node.parentNode;
    if (!kind || !src) {
      if (parent) parent.removeChild(node);
      return;
    }
    if (kind === 'pdf') {
      const link = pdfDownloadLink((node as unknown as { ownerDocument: LinkDocument }).ownerDocument, src);
      if (parent) parent.replaceChild(link as never, node);
      return;
    }
    node.setAttribute('sandbox', PROVIDER_SANDBOX_FLAGS);
    node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    node.setAttribute('loading', 'lazy');
    node.removeAttribute('target');
    node.removeAttribute('rel');
  });

  try {
    return DOMPurify.sanitize(html, {
      ...FORUM_SANITIZE_CONFIG,
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['frameborder', 'allowfullscreen', 'allow', 'sandbox', 'referrerpolicy', 'loading'],
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
};
